import { useMatch, useParams } from 'react-router';
import { useContainers } from '../../api/queries';
import type { Container } from '../../api/types';
import { readLastContainer } from './lastContainer';

/**
 * The container the viewer is in: the URL's, else the last one they had open, else the
 * tenant's default. Usable anywhere, the shell included. Each candidate counts only if it is still one of the tenant's
 * containers, so a deleted one is never where anything goes. `current` is undefined only
 * while the list is loading or could not be read.
 */
export function useCurrentContainer(): {
  current: Container | undefined;
  containers: ReturnType<typeof useContainers>;
} {
  // Matched against the location, not read from the route's params: the nav renders in
  // the shell, above the route that matches `:containerId`, where useParams has none.
  const containerId = useMatch('/containers/:containerId/*')?.params.containerId;
  const containers = useContainers();
  const list = containers.data;
  const find = (id: string | null | undefined) => (id ? list?.find((c) => c.id === id) : undefined);
  const current = find(containerId) ?? find(readLastContainer()) ?? list?.find((c) => c.is_default) ?? list?.[0];
  return { current, containers };
}

/**
 * The container of a page under `/containers/:containerId`. ContainerLayout has already
 * checked it is one of the tenant's, so a page can use it without asking.
 */
export function useContainerId(): string {
  const { containerId } = useParams();
  if (!containerId) throw new Error('useContainerId outside a /containers/:containerId route');
  return containerId;
}
